import React from "react";
import { motion } from "framer-motion";
import { Award, Leaf } from "lucide-react";
import { useApp } from "@/context/AppStore";
import { Panel, AnimatedNumber } from "@/components/common/Widgets";
import { fmtNum, fmtINR, fmtLakh } from "@/lib/format";

const PRODUCTS = [
  { key: "meg", name: "MEG", full: "Mono Ethylene Glycol", color: "#33b76d" },
  { key: "deg", name: "DEG", full: "Di Ethylene Glycol", color: "#1d6fb8" },
  { key: "teg", name: "TEG", full: "Tri Ethylene Glycol", color: "#9b5de5" },
];

export default function ProductionIntelligence() {
  const { snapshot } = useApp();
  if (!snapshot) return <div className="p-10 text-ink-soft font-mono">Loading…</div>;
  const prod = snapshot.production;
  const sus = snapshot.sustainability;
  const mp = prod.most_profitable;

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-4 animate-fade-up" data-testid="production-intelligence">
      <div>
        <div className="eyebrow">Production Intelligence</div>
        <h1 className="font-display font-extrabold text-2xl uppercase tracking-tight text-ink">Business Output, Not Process Tags</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {PRODUCTS.map((p, i) => {
          const d = prod[p.key];
          const isTop = mp === p.key;
          return (
            <motion.div key={p.key} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className="panel relative overflow-hidden" data-testid={`product-${p.key}`} style={isTop ? { borderColor: `${p.color}80` } : {}}>
              {isTop && (
                <div className="absolute top-0 right-0 flex items-center gap-1 px-2 py-1 text-[0.6rem] font-mono font-bold uppercase tracking-wider"
                  style={{ background: p.color, color: "#04210f" }}>
                  <Award size={11} /> Most Profitable
                </div>
              )}
              <div className="px-4 pt-4 pb-3 border-b border-border">
                <div className="flex items-baseline gap-2">
                  <span className="font-display font-extrabold text-2xl tracking-tight" style={{ color: p.color }}>{p.name}</span>
                  <span className="text-[0.66rem] text-ink-soft font-mono">{p.full}</span>
                </div>
                <div className="mt-1 flex items-baseline gap-1">
                  <span className="text-3xl font-bold tnum text-ink"><AnimatedNumber value={d.production_mt} decimals={1} /></span>
                  <span className="text-xs text-ink-soft font-mono">MT today</span>
                </div>
              </div>
              <div className="p-4 grid grid-cols-2 gap-3">
                <Stat label="Energy / Tonne" value={fmtNum(d.energy_per_t, 2)} unit="GJ" />
                <Stat label="Cost / Tonne" value={fmtINR(d.cost_per_t)} />
                <Stat label="Carbon / Tonne" value={fmtNum(d.carbon_per_t, 3)} unit="tCO₂" />
                <Stat label="Margin / Tonne" value={fmtINR(d.margin_per_t)} color={p.color} />
                <div className="col-span-2 panel-2 px-3 py-2 flex items-center justify-between">
                  <span className="eyebrow">Contribution Today</span>
                  <span className="text-base font-bold tnum" style={{ color: p.color }}>{fmtLakh(d.contribution)}</span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Panel eyebrow="Sustainability" title="Carbon Footprint" className="lg:col-span-2" testid="carbon-panel"
          right={<Leaf size={16} className="text-brand-green" />}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Scope 1 (Coal)" value={fmtNum(sus.scope1_tco2)} unit="tCO₂" color="#E63946" />
            <Stat label="Scope 2 (Grid)" value={fmtNum(sus.scope2_tco2)} unit="tCO₂" color="#fab20b" />
            <Stat label="Total Emissions" value={fmtNum(sus.total_tco2)} unit="tCO₂" />
            <Stat label="Carbon Intensity" value={fmtNum(sus.carbon_intensity, 3)} unit="tCO₂/MT" color="#33b76d" />
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <Stat label="PCF · MEG" value={fmtNum(sus.pcf_meg, 3)} unit="tCO₂/MT" />
            <Stat label="PCF · DEG" value={fmtNum(sus.pcf_deg, 3)} unit="tCO₂/MT" />
            <Stat label="PCF · TEG" value={fmtNum(sus.pcf_teg, 3)} unit="tCO₂/MT" />
          </div>
        </Panel>

        <Panel eyebrow="Specific Energy Consumption" title="SEC" testid="sec-panel">
          <div className="space-y-2.5">
            <Stat label="Thermal SEC · MEG" value={fmtNum(snapshot.sec.thermal_meg, 2)} unit="t steam/t" />
            <Stat label="Thermal SEC · DEG" value={fmtNum(snapshot.sec.thermal_deg, 2)} unit="t steam/t" />
            <Stat label="Thermal SEC · TEG" value={fmtNum(snapshot.sec.thermal_teg, 2)} unit="t steam/t" />
            <Stat label="Electrical SEC" value={fmtNum(snapshot.sec.electrical_sec, 1)} unit="kWh/t" color="#fab20b" />
          </div>
        </Panel>
      </div>
    </div>
  );
}

function Stat({ label, value, unit, color = "#FFFFFF" }) {
  return (
    <div className="panel-2 px-3 py-2.5">
      <div className="eyebrow truncate">{label}</div>
      <div className="flex items-baseline gap-1 mt-0.5">
        <span className="text-base font-bold tnum" style={{ color }}>{value}</span>
        {unit && <span className="text-[0.6rem] text-ink-soft font-mono">{unit}</span>}
      </div>
    </div>
  );
}
